// The bell's pure helpers (components/notifications/NotificationBell.jsx): the server's answer
// (GET notifications/, padihat/rtct/notify_views.py) read defensively, times in words, and what this browser can do
// about phone notifications. The browser glue (service worker, subscription) is services/pushClient.js.

const str = (v) => (typeof v === "string" && v ? v : null);
const KINDS = ["new", "back", "reset"];

// {notifications: [{id, kind, title, body, at (ms), unread, url, node}], unread, push: {available, publicKey,
// endpoints, problem}}, or null for anything that is not the endpoint's answer.
export function readNotifications(data) {
  if (!data || typeof data !== "object" || !Array.isArray(data.notifications)) return null;
  const notifications = data.notifications
    .filter((n) => n && Number.isFinite(n.id) && str(n.title))
    .map((n) => {
      const at = Date.parse(n.at || n.created_at || "");
      const device = n.device && typeof n.device === "object" ? n.device : {};
      return {
        id: n.id,
        kind: KINDS.includes(n.kind) ? n.kind : "other",
        title: n.title,
        body: str(n.body) || "",
        at: Number.isFinite(at) ? at : null,
        unread: n.unread === true,
        url: str(n.url) && n.url.startsWith("/") ? n.url : null,
        node: str(device.name) || str(device.mac) || "",
      };
    });
  const push = data.push && typeof data.push === "object" ? data.push : {};
  return {
    notifications,
    unread: Number.isFinite(data.unread) && data.unread > 0 ? Math.round(data.unread) : 0,
    push: {
      available: push.available === true && Boolean(str(push.public_key)),
      publicKey: str(push.public_key),
      endpoints: Array.isArray(push.subscriptions) ? push.subscriptions.map((s) => s && str(s.endpoint)).filter(Boolean) : [],
      problem: str(push.problem),
    },
  };
}

// "just now", "5 min ago", "3 h ago", "2 days ago", then the date.
export function timeAgo(ms, now = Date.now()) {
  if (!Number.isFinite(ms)) return "";
  const s = Math.max(0, (now - ms) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  if (s < 7 * 86400) {
    const d = Math.floor(s / 86400);
    return `${d} day${d === 1 ? "" : "s"} ago`;
  }
  return new Date(ms).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

// The badge's number: 1-9, then "9+".
export const badgeText = (n) => (n > 9 ? "9+" : String(n));

// What this browser can do about phone notifications, from what it exposes ({serviceWorker, pushManager,
// notification: Notification.permission or null, ios, standalone}): "ready", "denied", "install" (an iPhone or iPad
// browser tab: Apple allows push only to a site added to the Home Screen) or "unsupported".
export function pushState(env) {
  const can = env && env.serviceWorker && env.pushManager && env.notification !== null && env.notification !== undefined;
  if (!can) return env && env.ios && !env.standalone ? "install" : "unsupported";
  if (env.notification === "denied") return "denied";
  return "ready";
}

// The server's public key (base64url) as the bytes PushManager.subscribe() wants.
export function base64UrlToBytes(text) {
  const b64 = String(text).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b64 + "=".repeat((4 - (b64.length % 4)) % 4));
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

// Whether a subscription was made with this key (a server whose key changed needs a new one).
export function sameKey(buffer, publicKey) {
  if (!buffer || !publicKey) return false;
  const a = new Uint8Array(buffer);
  const b = base64UrlToBytes(publicKey);
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

// A short name for this device in the server's list: "iPhone, installed app", "Android, Chrome", "Windows, Edge".
export function deviceLabel(userAgent = "", standalone = false) {
  const ua = String(userAgent);
  const os = /iPhone/.test(ua) ? "iPhone" : /iPad/.test(ua) ? "iPad" : /Android/.test(ua) ? "Android"
    : /Windows/.test(ua) ? "Windows" : /Mac OS X|Macintosh/.test(ua) ? "Mac" : /Linux/.test(ua) ? "Linux" : "A device";
  const browser = /Edg\//.test(ua) ? "Edge" : /Firefox\//.test(ua) ? "Firefox" : /Chrome\//.test(ua) ? "Chrome"
    : /Safari\//.test(ua) ? "Safari" : "a browser";
  return standalone ? `${os}, installed app` : `${os}, ${browser}`;
}
